import { createTheme } from "@mui/material/styles";
import { lightBlue, blue, red, green, orange, cyan } from "@mui/material/colors";

// SCSS Color Variables (restored from original _colors.scss)
const navFooterBG = "#000000"; // $NavBG
const myColor = "rgb(11, 55, 124)"; // $myColor - this was used for h1 color only, not primary brand
const primaryMain = "#000000"; // The actual brand color was black
const navItem = "#f8f8ff"; // $NavItem
const linkHover = "rgb(136, 226, 253)"; // $LinkHover
const pokerRed = "rgb(255, 38, 0)"; // $red
const darkRed = "rgb(190, 42, 16)"; // $darkRed
const lightRed = "rgb(255, 36, 36)"; // $lightRed
const pokerGreen = "rgb(2, 202, 29)"; // $green
const snowBlue = "rgb(19, 197, 197)"; // $snowBlue

// SCSS Gradients (converted from _colors.scss)
const gradients = {
  green: "linear-gradient(to right bottom, #005307, #066811, #0d7d1c, #149327, #19aa33, #1bb83e, #1dc749, #1dd654, #1ae060, #15ea6c, #0ef579, #00ff85)",
  blue: "radial-gradient(circle, #cff1fd, #c6f1fe, #bcf0ff, #b2f0ff, #a8f0ff)",
  red: "radial-gradient(circle, #ffffff, #f0eaf1, #e5d5df, #debfc7, #d4abab)",
  grey: "radial-gradient(circle, rgba(246, 253, 255, 1) 27%, rgba(188, 210, 214, 1) 58%, rgba(142, 153, 156, 1) 91%)",
};

// Create a theme instance.
const theme = createTheme({
  palette: {
    navfooter: {
      main: navFooterBG,
      contrastText: navItem,
    },
    primary: {
      main: primaryMain, // Original brand color was black
      light: "#333333", // Dark grey for light variant
      dark: "#000000", // Black for dark variant
      contrastText: "#fff",
    },
    secondary: {
      main: snowBlue, // Using SCSS snowBlue color
      light: "rgb(77, 218, 218)", // Calculated lighter shade
      dark: "rgb(13, 138, 138)", // Calculated darker shade
      contrastText: "#000",
    },
    error: {
      main: pokerRed, // Using SCSS red
      light: lightRed, // Using SCSS lightRed
      dark: darkRed, // Using SCSS darkRed
      contrastText: "#fff",
    },
    success: {
      main: pokerGreen, // Using SCSS green
      light: "rgb(51, 217, 74)", // Calculated lighter shade
      dark: "rgb(1, 142, 20)", // Calculated darker shade
      contrastText: "#000",
    },
    warning: {
      light: orange[300],
      main: orange[700],
      dark: orange[900],
    },
    info: {
      light: cyan[300],
      main: cyan[700],
      dark: cyan[900],
    },
    // Table color overrides
    table: {
      primary: {
        main: "#1369be", // Default table header blue from tables.scss
        dark: "#0f5299",
        contrastText: "white",
      },
      secondary: {
        main: "#cd071e", // Red table header from tables.scss
        dark: "#aa0519",
        contrastText: "white",
      },
      waitlist: {
        main: "#43e6f1", // Waitlist table header color
        contrastText: "black",
      },
      currentGame: {
        main: "rgb(123, 247, 123)", // Current game table header color
        contrastText: "black",
      },
    },
    // Action colors
    action: {
      hover: linkHover,
      selected: `${primaryMain}14`, // 8% opacity of black
      disabled: "rgba(0, 0, 0, 0.26)",
    },
    background: {
      default: "rgb(246, 253, 255)", // From App.scss - this matches the grey gradient starting color
      paper: "#fff",
    },
    // Color utilities for font classes
    utilities: {
      redFont: pokerRed,
      blueFont: "rgb(0, 132, 255)",
      greenFont: "rgb(138, 238, 143)",
    },
  },
  // Add gradients to theme
  gradients,
  // Typography configuration
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif', // System fonts for body text
    h1: {
      fontFamily: '"Anton", sans-serif', // Custom font only for h1 headers
      color: myColor, // Original SCSS brand blue for h1 styling
    },
    h2: {
      fontFamily: '"Anton", sans-serif', // Custom font only for h2 headers
    },
    h3: {
      fontFamily: '"Anton", sans-serif', // Custom font only for h3 headers
    },
    h4: {
      fontFamily: '"Anton", sans-serif', // Custom font only for h4 headers
    },
    h5: {
      fontFamily: '"Anton", sans-serif', // Custom font only for h5 headers
    },
    h6: {
      fontFamily: '"Anton", sans-serif', // Custom font only for h6 headers
    },
    // Font utilities - only for special cases, not general use
    redFont: {
      fontFamily: '"Courgette", cursive',
      color: pokerRed,
      textAlign: 'center',
      textShadow: '2px 1px #000000',
      WebkitTextStroke: '1px black',
    },
    blueFont: {
      fontFamily: '"Courgette", cursive',
      color: 'rgb(0, 132, 255)',
      textAlign: 'center',
      textShadow: '2px 1px #000000',
      WebkitTextStroke: '1px black',
    },
    greenFont: {
      fontFamily: '"Courgette", cursive',
      color: 'rgb(138, 238, 143)',
      textAlign: 'center',
      textShadow: '2px 1px #000000',
      WebkitTextStroke: '1px black',
    },
  },
  // Breakpoints for responsive design (matching SCSS media queries)
  breakpoints: {
    values: {
      xs: 0,
      sm: 600, // Match App.scss media query
      md: 768,
      lg: 1024,
      xl: 1200,
    },
  },
  // Custom theme properties moved to theme.components for MUI v5+
  components: {
    // CssBaseline for global styles
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
          textAlign: 'center',
          background: gradients.grey,
          fontSize: {
            xs: '70%', // Media query from App.scss
            sm: '100%',
          },
        },
        // Global h1 styling
        h1: {
          color: myColor,
        },
        // Utility classes
        '.superscript': {
          color: 'red',
          verticalAlign: 'super',
          fontSize: 'medium',
        },
        '.form-styles': {
          textAlign: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
          borderCollapse: 'collapse',
          marginLeft: 'auto',
          marginRight: 'auto',
        },
        '.form-text-input': {
          backgroundColor: 'white',
          textAlign: 'center',
          margin: 'auto',
          width: '100%',
        },
        '.ql-editor': {
          minHeight: '100px !important',
          maxHeight: '300px',
          overflow: 'hidden',
          overflowY: 'scroll',
        },
        // Chat styles
        '.chat-container': {
          border: '2px solid #505050',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
          backgroundColor: '#f1f1f1',
          borderRadius: '5px',
          padding: '10px',
          width: '60%',
          margin: '20px 0',
        },
        '.sender': {
          backgroundColor: '#7dc0ff',
          marginLeft: '40%',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          marginBottom: "10px",
        },
      },
    },
    // Enhanced Table styling (from tables.scss)
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: "20px",
          border: "none",
          overflow: "hidden",
          // Default table styling
          '&.tablelist': {
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
            borderCollapse: 'collapse',
            border: 'none',
            width: '80%',
            margin: '0 auto',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiTableCell-root": {
            borderBottom: "none",
            border: 'none',
            padding: '8px',
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            padding: '12px 8px',
            textAlign: 'center',
            backgroundColor: '#1369be', // Default blue
            color: 'white',
          },
          // Table row striping
          "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
            backgroundColor: '#ffffff',
          },
          "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
            backgroundColor: 'rgb(190, 190, 190)',
          },
          "& .MuiTableBody-root .MuiTableRow-root:hover": {
            backgroundColor: 'rgb(145, 145, 145)',
          },
          // Table variants
          '&.waitlist-table .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: '#43e6f1',
            color: 'black',
          },
          '&.current-game-table .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: 'rgb(123, 247, 123)',
            color: 'black',
          },
          '&.requests-table .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: '#cd071e',
            color: 'white',
          },
          '&.private-table .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: '#cd071e',
            color: 'white',
          },
          // Highlight class
          '& .highlight': {
            border: 'none',
            backgroundColor: 'yellow',
          },
        },
      },
    },
    // Enhanced Button styling (from buttons.scss)
    MuiButton: {
      variants: [
        // Red button variant
        {
          props: { variant: 'redButton' },
          style: {
            fontSize: 'clamp(16px, 4vw, 36px)',
            textShadow: '2px 2px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
            WebkitTextStroke: '1px black',
            border: 'black',
            outline: 'none',
            padding: '12px',
            color: 'rgb(255, 255, 255)',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              boxShadow: '0 12px 16px 0 rgba(0, 0, 0, 0.24), 0 17px 50px 0 rgba(0, 0, 0, 0.19)',
            }
          },
        },
        // Dashboard button variants
        {
          props: { variant: 'dashButton' },
          style: {
            fontSize: 'clamp(12px, 2.5vw, 24px)',
            textShadow: '2px 2px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
            WebkitTextStroke: '1px black',
            border: 'black',
            outline: 'none',
            padding: '12px',
            flex: 1,
            color: 'rgb(255, 255, 255)',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              boxShadow: '0 12px 16px 0 rgba(0, 0, 0, 0.24), 0 17px 50px 0 rgba(0, 0, 0, 0.19)',
            },
            // Responsive sizing
            '@media (max-width: 768px)': {
              fontSize: '10px',
              padding: '8px 4px',
              minHeight: '40px',
            },
            '@media (max-width: 600px)': {
              fontSize: '8px',
              padding: '6px 2px',
              minHeight: '36px',
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          // Button color variants
          '&.gamelist-btn': {
            backgroundColor: snowBlue,
            '&:hover': {
              backgroundColor: 'rgb(77, 218, 218)',
            },
          },
          '&.player-dash-btn': {
            backgroundColor: pokerRed,
            '&:hover': {
              backgroundColor: lightRed,
            },
          },
          '&.create-game-btn': {
            backgroundColor: pokerGreen,
            '&:hover': {
              backgroundColor: 'rgb(51, 217, 74)',
            },
          },
          '&.manage-game-btn': {
            backgroundColor: snowBlue,
            '&:hover': {
              backgroundColor: 'rgb(77, 218, 218)',
            },
          },
          '&.manage-player-btn': {
            backgroundColor: lightRed,
            '&:hover': {
              backgroundColor: pokerRed,
            },
          },
          // Selected state
          '&.selected': {
            border: 'none',
            backgroundColor: 'yellow',
          },
        },
      },
    },
    // Pagination styling
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            border: 'none',
            borderRadius: '5px',
            backgroundColor: 'white',
            padding: '4px 10px',
            margin: '0 2px',
            '&:hover': {
              backgroundColor: '#ddd',
              transition: 'background-color 0.3s',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgb(145, 223, 233)',
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'rgb(125, 203, 213)',
              },
            },
          },
        },
      },
    },
    // Modal styling (replacing custom modal)
    MuiModal: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)', // From App.scss .modal
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          width: '80%',
          maxWidth: 'none',
        },
      },
    },
    // AppBar styling (navbar)
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: navFooterBG,
          position: 'sticky',
          zIndex: 100,
          paddingTop: '10px',
          '& .MuiToolbar-root': {
            display: 'flex',
            alignItems: 'stretch',
            flexGrow: 1,
            padding: 0,
          },
          '& .MuiButton-root': {
            color: navItem,
            background: 'none',
            border: 'none',
            marginTop: '10px',
            marginLeft: '10px',
            '&:hover': {
              color: linkHover,
              textDecoration: 'none',
            },
          },
        },
      },
    },
  },
  // Custom properties for backwards compatibility
  table: {
    defaultContainer: {
      textAlign: "center",
      margin: "auto",
      padding: "auto",
      borderRadius: "20px",
    },
  },
  header: {
    textAlign: "center",
    borderRadius: "5px 5px 0px 0px",
    marginBottom: "12px",
    padding: "12px 24px",
    color: "white",
    backgroundColor: primaryMain,
  },
  container: {
    padding: "12px 24px",
  },
});

export default theme;
